/**
 * The Template roster — one screen for both artifacts (#241 candidate B4).
 *
 * A Company's named Templates for one artifact: the list with its Default
 * badge and section counts, the quiet Company-default select (the floor is
 * always on offer), the inline create form under the list, and the delete
 * confirm that keeps a 409 refusal inside its modal. Opening a row hands the
 * Template to the artifact's studio.
 *
 * Everything artifact-specific arrives through one adapter: the copy, the
 * DOM id prefix, and the four API calls. Nothing here knows what a quote or
 * a job sheet is.
 */
import { useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import {
  ChevronLeftIcon,
  PencilIcon,
  TrashIcon,
} from "../../components/ui/icons";
import { SelectSettingRow } from "../../components/ui/SettingRow";
import forms from "../../components/ui/field.module.css";
import { cx } from "@shared/cx";
import { Loading, SectionHeader, SettingsGroup } from "./section-chrome";
import { InlineBanner } from "../../components/ui/InlineBanner";
import badge from "../../lib/status-badge.module.css";
import chrome from "./settings.module.css";
import styles from "./TemplateRoster.module.css";

/** The shape every Template row shares, whatever it configures. */
export interface RosterTemplate {
  id: string;
  name: string;
  is_default: boolean;
}

/** One artifact's half of the roster: its copy and its four API calls. */
export interface RosterArtifact<T extends RosterTemplate> {
  /** Prefixes the group heading, default select, and name-input DOM ids. */
  idPrefix: string;
  title: string;
  description: string;
  /** The default row's one-line description. */
  defaultDescription: string;
  /** The "no template" option — resolution falls back to the section floor. */
  floorLabel: string;
  /** Shown when the Company has no templates yet. */
  emptyText: string;
  /** The delete confirm's line after "Delete <name>?". */
  deleteWarning: string;
  /** The roster row's second line, e.g. "8 of 15 sections". */
  countLine: (template: T) => string;
  list: (signal?: AbortSignal) => Promise<T[]>;
  create: (name: string) => Promise<T>;
  remove: (id: string) => Promise<void>;
  /** Promote (or demote) one Template; the backend owns the swap. */
  setDefault: (template: T, isDefault: boolean) => Promise<T>;
}

/** How many of a section map's toggles are on. */
export function sectionCount(sections: Record<string, boolean>): string {
  const keys = Object.keys(sections);
  return `${keys.filter((key) => sections[key]).length} of ${keys.length}`;
}

function DefaultBadge() {
  return (
    <span className={cx(badge.badge, badge.blue)}>
      Default
    </span>
  );
}

function sortTemplates<T extends RosterTemplate>(rows: T[]): T[] {
  return [...rows].sort((a, b) =>
    a.is_default !== b.is_default
      ? a.is_default
        ? -1
        : 1
      : a.name.localeCompare(b.name),
  );
}

const message = (err: unknown) =>
  err instanceof Error ? err.message : String(err);

export function TemplateRoster<T extends RosterTemplate>({
  artifact,
  onBack,
  renderStudio,
}: {
  artifact: RosterArtifact<T>;
  /** Returns to the Documents & emails gallery from the templates list. */
  onBack?: () => void;
  /** The artifact's studio, rendered in place of the list while one is open. */
  renderStudio: (studio: {
    template: T;
    onSaved: (next: T) => void;
    onBack: () => void;
  }) => React.ReactNode;
}) {
  const [templates, setTemplates] = useState<T[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  // Inline create form (a creation form keeps its submit button — it is not
  // a setting).
  const [formOpen, setFormOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Each artifact's adapter is a module constant, so this runs once per mount.
  const listTemplates = artifact.list;
  useEffect(() => {
    const ac = new AbortController();
    listTemplates(ac.signal)
      .then((rows) => {
        setTemplates(sortTemplates(rows));
        setError(null);
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(message(err));
      });
    return () => ac.abort();
  }, [listTemplates]);

  /** Merge a saved template back in; a promoted default demotes the rest. */
  const replaceTemplate = (next: T) =>
    setTemplates((rows) =>
      rows
        ? sortTemplates(
            rows.map((row) =>
              row.id === next.id
                ? next
                : next.is_default
                  ? { ...row, is_default: false }
                  : row,
            ),
          )
        : rows,
    );

  const create = async () => {
    setCreating(true);
    try {
      const created = await artifact.create(draftName.trim());
      setTemplates((rows) => sortTemplates([...(rows ?? []), created]));
      setFormOpen(false);
      setDraftName("");
      setCreateError(null);
      setOpenId(created.id);
    } catch (err) {
      setCreateError(message(err));
    } finally {
      setCreating(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await artifact.remove(deleteTarget.id);
      setTemplates(
        (rows) => rows?.filter((row) => row.id !== deleteTarget.id) ?? rows,
      );
      setDeleteTarget(null);
      setDeleteError(null);
    } catch (err) {
      setDeleteError(message(err));
    } finally {
      setDeleting(false);
    }
  };

  // One quiet select over the roster: picking a template promotes it (the
  // backend demotes any other in the same tx); the floor option demotes the
  // current default so resolution falls back to the everything-on floor.
  const setDefaultTemplate = async (next: string) => {
    if (next === "") {
      const current = templates?.find((t) => t.is_default) ?? null;
      if (!current) return;
      replaceTemplate(await artifact.setDefault(current, false));
      return;
    }
    const target = templates?.find((t) => t.id === next);
    if (!target) return;
    replaceTemplate(await artifact.setDefault(target, true));
  };

  const open = templates?.find((t) => t.id === openId) ?? null;
  if (open) {
    return (
      <>
        {renderStudio({
          template: open,
          onSaved: replaceTemplate,
          onBack: () => setOpenId(null),
        })}
      </>
    );
  }

  return (
    <section className={chrome.section}>
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className={chrome.backLink}
        >
          <ChevronLeftIcon className={chrome.backIcon} />
          All documents &amp; emails
        </button>
      )}
      <SectionHeader
        title={artifact.title}
        description={artifact.description}
      />

      <SettingsGroup
        id={`${artifact.idPrefix}-templates-heading`}
        title="Templates"
        action={
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setFormOpen(true)}
          >
            Add template
          </Button>
        }
      >
        {error && (
          <p role="alert" className={chrome.sectionError}>
            {error}
          </p>
        )}

        {!templates ? (
          !error && <Loading />
        ) : templates.length === 0 ? (
          <p className={chrome.empty}>{artifact.emptyText}</p>
        ) : (
          <>
            <SelectSettingRow
              id={`${artifact.idPrefix}-default-template`}
              title="Company default"
              description={artifact.defaultDescription}
              value={templates.find((t) => t.is_default)?.id ?? ""}
              options={[
                { value: "", label: artifact.floorLabel },
                ...templates.map((t) => ({ value: t.id, label: t.name })),
              ]}
              onChange={setDefaultTemplate}
            />
            <ul className={chrome.list}>
              {templates.map((t) => (
                <li
                  key={t.id}
                  className={chrome.row}
                >
                  <div className={chrome.rowText}>
                    <p className={styles.nameLine}>
                      <span className={chrome.truncate}>{t.name}</span>
                      {t.is_default && <DefaultBadge />}
                    </p>
                    <p className={chrome.rowMeta}>
                      {artifact.countLine(t)}
                    </p>
                  </div>
                  <div className={chrome.rowActions}>
                    <button
                      type="button"
                      aria-label={`Edit ${t.name}`}
                      className={chrome.iconButton}
                      onClick={() => setOpenId(t.id)}
                    >
                      <PencilIcon className={chrome.icon} />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${t.name}`}
                      className={chrome.iconButton}
                      onClick={() => {
                        setDeleteError(null);
                        setDeleteTarget(t);
                      }}
                    >
                      <TrashIcon className={chrome.icon} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* The new-template form sits under the existing ones: it appends to
            the list, so it belongs where the new row will appear. */}
        {formOpen && (
          <div className={chrome.panel}>
            <label
              className={forms.label}
              htmlFor={`${artifact.idPrefix}-template-name`}
            >
              Name
            </label>
            <input
              id={`${artifact.idPrefix}-template-name`}
              className={forms.input}
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />
            {createError && (
              <InlineBanner variant="error">{createError}</InlineBanner>
            )}
            <div className={chrome.panelActions}>
              <Button
                size="sm"
                loading={creating}
                disabled={draftName.trim() === ""}
                onClick={() => void create()}
              >
                Create template
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setFormOpen(false);
                  setCreateError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        <ConfirmModal
          open={deleteTarget !== null}
          title="Delete template"
          message={
            <>
              Delete <strong>{deleteTarget?.name ?? ""}</strong>?{" "}
              {artifact.deleteWarning}
            </>
          }
          error={deleteError}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deleting}
          onConfirm={() => void remove()}
          onCancel={() => {
            setDeleteTarget(null);
            setDeleteError(null);
          }}
        />
      </SettingsGroup>
    </section>
  );
}
